import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, Star, ShieldCheck, Lock, Zap } from 'lucide-react';
/**
 * 👉 REEMPLAZÁ estos valores por los links reales de checkout de Mercado Pago.
 *    CHECKOUT_BASE_URL     → Edición Base ($14.990)
 *    CHECKOUT_COMPLETE_URL → Edición Completa ($19.990)
 */
const CHECKOUT_BASE_URL = 'https://checkout.nichodigital.shop/cart/51480683413782:1';
const CHECKOUT_COMPLETE_URL = 'https://checkout.nichodigital.shop/cart/51480683381014:1';
type PlanId = 'base' | 'complete';
interface Plan {
  id: PlanId;
  name: string;
  oldPrice: string;
  price: string;
  summary: string;
  features: string[];
  checkoutUrl: string;
}
const PLANS: Plan[] = [{
  id: 'base',
  name: 'Edición Base',
  oldPrice: '$24.990',
  price: '$14.990',
  summary: 'Los 4 tomos principales para cocinar bien desde cero.',
  features: ['4 tomos principales', 'PDF · acceso inmediato', 'Garantía 7 días'],
  checkoutUrl: CHECKOUT_BASE_URL
}, {
  id: 'complete',
  name: 'Edición Completa',
  oldPrice: '$31.990',
  price: '$19.990',
  summary: 'Los 4 tomos + 2 tomos exclusivos (El Toque y El Remate).',
  features: ['6 tomos (incluye 2 exclusivos)', 'PDF · acceso inmediato', 'Garantía 7 días'],
  checkoutUrl: CHECKOUT_COMPLETE_URL
}];
interface OfferSelectorProps {
  ctaLabel?: string;
}
export function OfferSelector({
  ctaLabel = '💥 Quiero mi Operación Suegra AHORA'
}: OfferSelectorProps) {
  // La opción de mayor precio aparece preseleccionada.
  const [selected, setSelected] = useState<PlanId>('complete');
  const activePlan = PLANS.find(p => p.id === selected) ?? PLANS[1];
  return <div className="w-full" data-id="element-144">
      <div className="grid grid-cols-2 gap-3 md:gap-4" role="radiogroup" aria-label="Elegí tu edición" data-id="element-145">
        {PLANS.map(plan => {
        const isSelected = selected === plan.id;
        const isComplete = plan.id === 'complete';
        return <motion.button key={plan.id} type="button" role="radio" aria-checked={isSelected} onClick={() => setSelected(plan.id)} whileTap={{
          scale: 0.98
        }} className={`relative text-left rounded-2xl p-4 md:p-6 border-2 transition-all flex flex-col focus:outline-none focus:ring-2 focus:ring-brand-gold/50 ${isSelected ? isComplete ? 'border-brand-gold bg-brand-gold/10 shadow-[0_0_30px_rgba(232,185,35,0.25)]' : 'border-brand-gold bg-brand-gold/5' : 'border-brand-border bg-brand-panel/60 hover:border-brand-gold/40'}`} data-id="element-146">
              {isComplete && <span className="absolute -top-2.5 md:-top-3 left-1/2 -translate-x-1/2 bg-brand-gold text-brand-dark font-bold px-2.5 md:px-4 py-0.5 md:py-1 rounded-full text-[9px] md:text-xs uppercase tracking-wider whitespace-nowrap shadow-lg" data-id="element-147">
                  ⭐ Más Elegida
                </span>}

              {/* Radio indicator */}
              <span className={`absolute top-3 right-3 md:top-4 md:right-4 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${isSelected ? 'border-brand-gold bg-brand-gold' : 'border-brand-border'}`} data-id="element-148">
                {isSelected && <Check className="w-3 h-3 text-brand-dark" strokeWidth={3} data-id="element-149" />}
              </span>

              <p className={`uppercase tracking-wider text-[10px] md:text-sm font-bold mb-2 mt-1 ${isComplete ? 'text-brand-gold' : 'text-gray-400'}`} data-id="element-150">
                {plan.name}
              </p>

              <span className="text-gray-500 line-through text-xs md:text-sm block" data-id="element-151">
                {plan.oldPrice}
              </span>
              <div className="flex items-baseline gap-1 mb-2 md:mb-3" data-id="element-152">
                <span className="text-2xl md:text-4xl font-bold text-white" data-id="element-153">
                  {plan.price}
                </span>
                <span className="text-brand-gold text-[10px] md:text-xs font-bold" data-id="element-154">
                  ARS
                </span>
              </div>

              <p className="text-[11px] md:text-sm text-gray-400 mb-3 md:mb-4 leading-snug" data-id="element-155">
                {plan.summary}
              </p>

              <ul className="space-y-1.5 md:space-y-2 mt-auto" data-id="element-156">
                {plan.features.map((f, i) => <li key={i} className="flex items-start gap-1.5 md:gap-2 text-[11px] md:text-sm" data-id="element-157">
                    <Check className={`w-3.5 h-3.5 md:w-4 md:h-4 flex-shrink-0 mt-0.5 ${isComplete ? 'text-brand-gold' : 'text-gray-500'}`} data-id="element-158" />
                    <span className={isComplete ? 'text-gray-200' : 'text-gray-400'} data-id="element-159">
                      {f}
                    </span>
                  </li>)}
              </ul>
            </motion.button>;
      })}
      </div>

      {/* Shared purchase button — redirige según la opción elegida */}
      <motion.a key={activePlan.id} href={activePlan.checkoutUrl} whileHover={{
      scale: 1.02
    }} whileTap={{
      scale: 0.98
    }} className="mt-5 md:mt-6 w-full flex flex-col items-center justify-center text-center bg-brand-red hover:bg-brand-redDark text-white font-bold py-4 md:py-5 px-6 rounded-xl shadow-[0_0_25px_rgba(192,57,43,0.45)] transition-colors" data-id="element-160">
        <span className="text-base md:text-xl" data-id="element-161">{ctaLabel}</span>
        <span className="text-xs md:text-sm font-normal text-white/85 mt-0.5" data-id="element-162">
          {activePlan.name} · {activePlan.price} ARS
        </span>
      </motion.a>

      <div className="mt-4 md:mt-5 flex flex-wrap justify-center items-center gap-3 md:gap-6 text-[11px] md:text-sm text-gray-400" data-id="element-163">
        <div className="flex items-center gap-1.5" data-id="element-164">
          <Lock className="w-3.5 h-3.5 md:w-4 md:h-4" data-id="element-165" /> Pago seguro
        </div>
        <div className="flex items-center gap-1.5" data-id="element-166">
          <Check className="w-3.5 h-3.5 md:w-4 md:h-4" data-id="element-167" /> Mercado Pago
        </div>
        <div className="flex items-center gap-1.5" data-id="element-168">
          <Zap className="w-3.5 h-3.5 md:w-4 md:h-4" data-id="element-169" /> Acceso en 5 min
        </div>
        <div className="flex items-center gap-1.5" data-id="element-170">
          <ShieldCheck className="w-3.5 h-3.5 md:w-4 md:h-4" data-id="element-171" /> Garantía 7 días
        </div>
      </div>
    </div>;
}